import i18n from "../lang/i18n";

// Backend errors carry a stable `code` next to `detail` (e.g. "no_active_account", "folder_not_empty").
// Accepts an axios error or an axios response.
const errorData = (err) => err?.response?.data ?? err?.data;

// A validation error may carry the code as a list ({code: ["section_move"]}); the error of a field named `code`
// (e.g. a question code already taken) is a sentence, not a code.
const CODE = /^[a-z][a-z0-9_]*$/;
export const getApiErrorCode = (err) => {
  const code = errorData(err)?.code;
  const first = Array.isArray(code) ? code[0] : code;
  return typeof first === "string" && CODE.test(first) ? first : null;
};

// The booklets or tests an error or a passage's usage lists ([{uuid, name}]) as [{key, label}]: a name listed more
// than once reads once with its count ("Тест 1, БТС пәндік нұсқасы ×2"). A subject variant the user does not compose
// comes as {uuid: null, name: "Вариант БТС"}, always in Russian: it is told by its null uuid (a test that has that
// name stays apart) and named in the user's language. `t` defaults to the app's.
export const listedItems = (items, t = i18n.t.bind(i18n)) => {
  const counts = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const hidden = item?.uuid === null;
    const name = hidden ? t("variants.hidden_variant") : typeof item?.name === "string" ? item.name.trim() : "";
    if (!name) return;
    const key = hidden ? "hidden" : `name:${name}`;
    counts.set(key, { name, count: (counts.get(key)?.count || 0) + 1 });
  });
  return [...counts].map(([key, { name, count }]) => ({ key, label: count > 1 ? `${name} ×${count}` : name }));
};

// The same names, joined.
export const listedNames = (items, t) => listedItems(items, t).map((item) => item.label).join(", ");

// Translated message for the error's code (errors.<code>, interpolated with the response body and {{names}},
// the booklets or tests it lists), falling back to `message`, the page's own text: a 409 "conflict" then says
// to repeat the action instead of a generic "failed".
export const getCodedErrorMessage = (t, err, message) => {
  const data = errorData(err);
  const code = getApiErrorCode(err);
  if (!code) return message;
  const names = listedNames([data?.booklets, data?.tests].find(Array.isArray), t);
  return t(`errors.${code}`, { ...data, code, names, defaultValue: message });
};

// Same, falling back to the server's detail and then to `fallbackKey`.
export const getApiErrorMessage = (t, err, fallbackKey) => {
  const detail = errorData(err)?.detail;
  return getCodedErrorMessage(t, err, (typeof detail === "string" && detail) || t(fallbackKey));
};
